class PremiumClotheslineSlider extends HTMLElement {
  constructor() {
    super();
    this.current = 0;
    this.realIndex = 0;
    this.animating = false;
    this.dragging = false;
    this.dragged = false;
    this.autoplay = false;
    this.autoplayDirection = 1;
    this.isVisible = true;
    this.baseTranslate = 0;
    this.autoplayTimer = null;
    this.resizeFrame = null;
    this.transitionFallback = null;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.viewport = this.querySelector('[data-pcl-viewport]');
    this.track = this.querySelector('[data-pcl-track]');
    this.originalSlides = Array.from(this.querySelectorAll('[data-pcl-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-pcl-dot]'));
    this.previousButton = this.querySelector('[data-pcl-prev]');
    this.nextButton = this.querySelector('[data-pcl-next]');
    this.status = this.querySelector('[data-pcl-status]');
    this.linePath = this.querySelector('[data-pcl-line-path]');
    this.autoplayButton = this.querySelector('[data-pcl-autoplay-toggle]');
    this.autoplayText = this.querySelector('[data-pcl-autoplay-text]');
    this.autoplayPlayIcon = this.querySelector('[data-pcl-play-icon]');
    this.autoplayPauseIcon = this.querySelector('[data-pcl-pause-icon]');
    this.count = this.originalSlides.length;

    if (!this.viewport || !this.track || !this.count) return;

    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplayDelay = Math.max(2000, Number(this.dataset.autoplayDelay) || 2000);
    this.transitionDuration = Number(this.dataset.transitionDuration) || 700;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (this.count > 1) this.createLoop();
    else this.renderedSlides = this.originalSlides;

    this.updateAutoplayControlState();

    this.bindEvents();
    this.resizeObserver = new ResizeObserver(() => this.queueLayout());
    this.resizeObserver.observe(this.viewport);
    this.visibilityObserver = new IntersectionObserver(this.handleVisibility, { rootMargin: '120px', threshold: 0.08 });
    this.visibilityObserver.observe(this);

    requestAnimationFrame(() => {
      this.setPosition(false);
      this.updateSlideStates();
      this.startAutoplay();
    });
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    cancelAnimationFrame(this.resizeFrame);
    clearTimeout(this.transitionFallback);
    this.unbindEvents();
  }

  createLoop() {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    this.originalSlides.forEach((slide) => before.append(this.createClone(slide)));
    this.originalSlides.forEach((slide) => after.append(this.createClone(slide)));
    this.track.prepend(before);
    this.track.append(after);

    this.renderedSlides = Array.from(this.track.children);
    this.current = this.count;
  }

  createClone(slide) {
    const clone = slide.cloneNode(true);
    clone.dataset.pclClone = 'true';
    clone.removeAttribute('id');
    clone.removeAttribute('data-shopify-editor-block');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('img').forEach((image) => {
      image.loading = 'lazy';
      image.fetchPriority = 'low';
    });
    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
    return clone;
  }

  bindEvents() {
    this.onPrevious = () => this.go(-1, true);
    this.onNext = () => this.go(1, true);
    this.onKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1, true);
      }
    };
    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onPointerMove = this.handlePointerMove.bind(this);
    this.onPointerUp = this.handlePointerUp.bind(this);
    this.onTransitionEnd = this.handleTransitionEnd.bind(this);
    this.onVisibilityChange = () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    };
    this.onAutoplayToggle = () => this.toggleAutoplay();
    this.onClickCapture = (event) => {
      if (this.dragged) {
        event.preventDefault();
        event.stopPropagation();
        this.dragged = false;
      }
    };
    this.onBlockSelect = (event) => {
      const slide = event.target.closest?.('[data-pcl-slide]');
      if (!slide || !this.contains(slide)) return;
      const index = Number(slide.dataset.realIndex);
      if (Number.isFinite(index)) this.select(index, false);
    };

    this.previousButton?.addEventListener('click', this.onPrevious);
    this.nextButton?.addEventListener('click', this.onNext);
    this.viewport.addEventListener('keydown', this.onKeydown);
    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('pointermove', this.onPointerMove);
    this.viewport.addEventListener('pointerup', this.onPointerUp);
    this.viewport.addEventListener('pointercancel', this.onPointerUp);
    this.track.addEventListener('transitionend', this.onTransitionEnd);
    this.autoplayButton?.addEventListener('click', this.onAutoplayToggle);
    this.addEventListener('click', this.onClickCapture, true);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.select(Number(dot.dataset.pclDot), true)));
  }

  unbindEvents() {
    this.previousButton?.removeEventListener('click', this.onPrevious);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.viewport?.removeEventListener('keydown', this.onKeydown);
    this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
    this.viewport?.removeEventListener('pointermove', this.onPointerMove);
    this.viewport?.removeEventListener('pointerup', this.onPointerUp);
    this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
    this.track?.removeEventListener('transitionend', this.onTransitionEnd);
    this.autoplayButton?.removeEventListener('click', this.onAutoplayToggle);
    this.removeEventListener('click', this.onClickCapture, true);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  queueLayout() {
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      this.setPosition(false);
      this.updateSlideStates();
    });
  }

  getTranslate(index = this.current) {
    const slide = this.renderedSlides[index];
    if (!slide) return 0;
    return this.viewport.clientWidth / 2 - (slide.offsetLeft + slide.offsetWidth / 2);
  }

  setPosition(animate = true, dragOffset = 0) {
    if (!this.renderedSlides?.length) return;
    const shouldAnimate = animate && !this.reducedMotion.matches;
    this.track.style.transitionDuration = shouldAnimate ? `${this.transitionDuration}ms` : '0ms';
    this.baseTranslate = this.getTranslate();
    this.track.style.transform = `translate3d(${this.baseTranslate + dragOffset}px, 0, 0)`;
  }

  go(direction, userInitiated = false) {
    if (this.count <= 1 || this.animating) return;
    this.stopAutoplay();
    this.current += direction;
    this.realIndex = this.mod(this.realIndex + direction, this.count);
    this.animating = !this.reducedMotion.matches;
    this.setPosition(true);
    this.updateSlideStates();
    this.updateNavigation(userInitiated);

    clearTimeout(this.transitionFallback);
    this.transitionFallback = setTimeout(() => this.finishTransition(), this.transitionDuration + 80);
    if (!this.animating) this.finishTransition();
  }

  select(index, userInitiated = false) {
    if (!Number.isFinite(index) || index < 0 || index >= this.count || this.animating) return;
    if (index === this.realIndex) {
      this.setPosition(true);
      return;
    }

    this.stopAutoplay();
    let delta = index - this.realIndex;
    if (Math.abs(delta) > this.count / 2) delta += delta > 0 ? -this.count : this.count;
    this.current += delta;
    this.realIndex = index;
    this.animating = !this.reducedMotion.matches;
    this.setPosition(true);
    this.updateSlideStates();
    this.updateNavigation(userInitiated);

    clearTimeout(this.transitionFallback);
    this.transitionFallback = setTimeout(() => this.finishTransition(), this.transitionDuration + 80);
    if (!this.animating) this.finishTransition();
  }

  handleTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    this.finishTransition();
  }

  finishTransition() {
    clearTimeout(this.transitionFallback);
    if (this.count > 1) {
      while (this.current < this.count) this.current += this.count;
      while (this.current >= this.count * 2) this.current -= this.count;
    }
    this.animating = false;
    this.setPosition(false);
    this.updateSlideStates();
    this.startAutoplay();
  }

  getLineMetrics() {
    if (!this.linePath?.getTotalLength || !this.linePath?.getScreenCTM) return null;

    try {
      const length = this.linePath.getTotalLength();
      const matrix = this.linePath.getScreenCTM();
      if (!length || !matrix) return null;
      return { length, matrix };
    } catch (_error) {
      return null;
    }
  }

  getLineYAtX(screenX, metrics) {
    let start = 0;
    let end = metrics.length;
    let point = this.linePath.getPointAtLength(metrics.length / 2);

    // The clothesline path is monotonic from left to right. A short binary
    // search finds its exact screen-space height beneath each peg hole.
    for (let iteration = 0; iteration < 18; iteration += 1) {
      const distance = (start + end) / 2;
      point = this.linePath.getPointAtLength(distance);
      const pointX = metrics.matrix.a * point.x + metrics.matrix.c * point.y + metrics.matrix.e;
      if (pointX < screenX) start = distance;
      else end = distance;
    }

    point = this.linePath.getPointAtLength((start + end) / 2);
    return metrics.matrix.b * point.x + metrics.matrix.d * point.y + metrics.matrix.f;
  }

  syncPegPositions(trackTranslate = this.baseTranslate) {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const curveDepth = Number(mobile ? this.dataset.curveDepthMobile : this.dataset.curveDepthDesktop) || 0;
    const lineMetrics = this.getLineMetrics();
    const viewportRect = this.viewport.getBoundingClientRect();
    const viewportStyles = getComputedStyle(this.viewport);
    const trackTop = viewportRect.top + (parseFloat(viewportStyles.borderTopWidth) || 0) + (parseFloat(viewportStyles.paddingTop) || 0);
    const pegHoleOffset = parseFloat(getComputedStyle(this).getPropertyValue('--pcl-peg-hole-offset')) || -29;

    this.renderedSlides.forEach((slide, index) => {
      const distance = Math.abs(index - this.current);
      const cappedDistance = Math.min(distance, 2.4);
      let y = -curveDepth * Math.min(cappedDistance / 2, 1);

      if (lineMetrics) {
        const pegX = viewportRect.left + trackTranslate + slide.offsetLeft + slide.offsetWidth / 2;
        const lineY = this.getLineYAtX(pegX, lineMetrics);
        const alignedY = lineY - trackTop - pegHoleOffset;
        if (Number.isFinite(alignedY)) y = alignedY;
      }

      slide.style.setProperty('--arc-y', `${y}px`);
    });
  }

  updateSlideStates() {
    this.syncPegPositions();

    this.renderedSlides.forEach((slide, index) => {
      const distance = Math.abs(index - this.current);
      const active = distance === 0;
      const scale = active ? Number(getComputedStyle(this).getPropertyValue('--pcl-active-scale')) || 1 : Math.max(0.88, 0.96 - distance * 0.025);
      const opacity = distance > 3.4 ? 0.52 : Math.max(0.72, 1 - distance * 0.08);

      slide.style.setProperty('--card-scale', String(scale));
      slide.style.setProperty('--slide-opacity', String(opacity));
      slide.style.setProperty('--slide-z', String(Math.max(1, 20 - distance)));
      slide.dataset.active = String(active);
      slide.setAttribute('aria-hidden', String(!active));

      const interactive = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]');
      interactive.forEach((element) => {
        if (slide.dataset.pclClone === 'true' || !active) element.setAttribute('tabindex', '-1');
        else element.removeAttribute('tabindex');
      });
    });
  }

  updateNavigation(announce = false) {
    this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.realIndex)));
    if (announce && this.status) this.status.textContent = `Card ${this.realIndex + 1} of ${this.count}`;
  }

  toggleAutoplay() {
    if (this.count <= 1) return;
    this.autoplay = !this.autoplay;
    if (this.autoplay) this.startAutoplay();
    else this.stopAutoplay();
    this.updateAutoplayControlState();
  }

  updateAutoplayControlState() {
    if (!this.autoplayButton) return;
    const isPlaying = this.autoplay;

    this.autoplayButton.hidden = this.count <= 1;
    this.autoplayButton.setAttribute('aria-pressed', String(isPlaying));
    this.autoplayButton.setAttribute('aria-label', `${isPlaying ? 'Pause' : 'Play'} carousel autoplay`);
    if (this.autoplayText) this.autoplayText.textContent = isPlaying ? 'Pause' : 'Play';
    if (this.autoplayPlayIcon) this.autoplayPlayIcon.hidden = isPlaying;
    if (this.autoplayPauseIcon) this.autoplayPauseIcon.hidden = !isPlaying;
  }

  handlePointerDown(event) {
    if (this.count <= 1 || event.button !== 0 || this.animating) return;
    this.dragging = true;
    this.dragged = false;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragDelta = 0;
    this.stopAutoplay();
    this.track.style.transitionDuration = '0ms';
    this.viewport.setPointerCapture?.(event.pointerId);
  }

  handlePointerMove(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;
    this.dragDelta = event.clientX - this.dragStartX;
    if (Math.abs(this.dragDelta) > 6) this.dragged = true;
    this.track.style.transform = `translate3d(${this.baseTranslate + this.dragDelta}px, 0, 0)`;
    this.syncPegPositions(this.baseTranslate + this.dragDelta);
  }

  handlePointerUp(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;
    this.dragging = false;
    this.viewport.releasePointerCapture?.(event.pointerId);
    const threshold = Math.min(70, Math.max(34, this.viewport.clientWidth * 0.1));

    if (this.dragDelta <= -threshold) this.go(1, true);
    else if (this.dragDelta >= threshold) this.go(-1, true);
    else {
      this.setPosition(true);
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplay || this.count <= 1 || this.reducedMotion.matches || !this.isVisible || document.hidden) return;
    this.autoplayTimer = window.setTimeout(() => this.go(this.autoplayDirection, false), this.autoplayDelay);
  }

  stopAutoplay() {
    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  handleVisibility = (entries) => {
    this.isVisible = entries[0]?.isIntersecting ?? true;
    if (this.isVisible) this.startAutoplay();
    else this.stopAutoplay();
  };

  mod(value, length) {
    return ((value % length) + length) % length;
  }
}

if (!customElements.get('premium-clothesline-slider')) {
  customElements.define('premium-clothesline-slider', PremiumClotheslineSlider);
}
